import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class Vault {
  private readonly APP_SECRET = environment.appSecret;
  private readonly SALT = environment.salt;

  private enc = new TextEncoder();
  private dec = new TextDecoder();

  private async getKey(): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      this.enc.encode(this.APP_SECRET),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: this.enc.encode(this.SALT),
        iterations: 100_000,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  private b64(u8: Uint8Array) {
    return btoa(String.fromCharCode(...u8));
  }
  private ub64(s: string) {
    const bin = atob(s);
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
  }

  async set<T>(key: string, value: T): Promise<void> {
    const json = JSON.stringify(value);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const k = await this.getKey();
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, k, this.enc.encode(json));
    const payload = {
      iv: this.b64(iv),
      ct: this.b64(new Uint8Array(ct)),
      alg: 'AES-GCM',
      v: 1,
    };
    localStorage.setItem(key, JSON.stringify(payload));
  }

  async get<T>(key: string): Promise<T | null> {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const { iv, ct } = JSON.parse(raw);
      const k = await this.getKey();
      const pt = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: this.ub64(iv) },
        k,
        this.ub64(ct),
      );
      return JSON.parse(this.dec.decode(pt)) as T;
    } catch {
      return null;
    }
  }

  remove(key: string) {
    localStorage.removeItem(key);
  }
  clear() {
    localStorage.clear();
  }
}
